"use client";

import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  Divider,
} from "@mui/material";
import { createTheme, ThemeProvider, alpha } from "@mui/material/styles";
import {
  FaUserShield,
  FaLock,
  FaEye,
  FaEyeSlash,
  FaChartLine,
  FaUsersCog,
  FaShieldAlt,
  FaArrowRight,
  FaSun,
  FaMoon,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../hooks/reduxHooks";
import { loginUser, verifyOtp, cancelOtp } from "../../../features/auth/authSlice";
import { MdLockClock } from "react-icons/md";


/* ─── Google Font ─── */
const fontLink = document.createElement("link");
fontLink.rel = "stylesheet";
fontLink.href =
  "https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800&display=swap";
document.head.appendChild(fontLink);

/* ─── Keyframe Animations ─── */
const styleTag = document.createElement("style");
styleTag.innerHTML = `
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(24px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes fadeIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  @keyframes floatA {
    0%,100% { transform: translateY(0) scale(1) rotate(0deg); }
    50%      { transform: translateY(-32px) scale(1.04) rotate(2deg); }
  }
  @keyframes floatB {
    0%,100% { transform: translateY(0) scale(1) rotate(0deg); }
    50%      { transform: translateY(20px) scale(0.97) rotate(-2deg); }
  }
  @keyframes shimmer {
    0%   { background-position: -600px 0; }
    100% { background-position: 600px 0; }
  }
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  @keyframes pulseDot {
    0%,100% { opacity: 1; transform: scale(1); box-shadow: 0 0 0 0 rgba(34,197,94,0.4); }
    50%      { opacity: 0.8; transform: scale(1.1); box-shadow: 0 0 0 6px rgba(34,197,94,0); }
  }
  @keyframes gridMove {
    0%   { transform: translateY(0); }
    100% { transform: translateY(28px); }
  }
  @keyframes particleFloat {
    0%   { transform: translateY(0px) translateX(0px); opacity: 0; }
    10%  { opacity: 1; }
    90%  { opacity: 1; }
    100% { transform: translateY(-100vh) translateX(30px); opacity: 0; }
  }
  @keyframes beamSweep {
    0%   { transform: translateX(-100%) rotate(45deg); opacity: 0; }
    30%  { opacity: 0.6; }
    70%  { opacity: 0.6; }
    100% { transform: translateX(200%) rotate(45deg); opacity: 0; }
  }
  @keyframes hexPulse {
    0%,100% { opacity: 0.03; transform: scale(1); }
    50%      { opacity: 0.08; transform: scale(1.05); }
  }
  @keyframes ringExpand {
    0%   { transform: scale(0.8); opacity: 0.6; }
    100% { transform: scale(2.2); opacity: 0; }
  }
  @keyframes dashMove {
    0%   { stroke-dashoffset: 1000; }
    100% { stroke-dashoffset: 0; }
  }
  .card-animate  { animation: fadeUp 0.6s cubic-bezier(0.22,1,0.36,1) both; }
  .logo-animate  { animation: fadeIn 0.5s ease both; }
  .feature-1     { animation: fadeUp 0.55s cubic-bezier(0.22,1,0.36,1) 0.10s both; }
  .feature-2     { animation: fadeUp 0.55s cubic-bezier(0.22,1,0.36,1) 0.22s both; }
  .feature-3     { animation: fadeUp 0.55s cubic-bezier(0.22,1,0.36,1) 0.34s both; }
  .orb-a         { animation: floatA 13s ease-in-out infinite; }
  .orb-b         { animation: floatB 10s ease-in-out infinite; }
  .live-dot      { animation: pulseDot 2s ease infinite; }
  .grid-move     { animation: gridMove 3s linear infinite; }
  .beam-sweep    { animation: beamSweep 8s ease-in-out infinite; }
  .hex-pulse     { animation: hexPulse 4s ease-in-out infinite; }
  .ring-expand   { animation: ringExpand 3s ease-out infinite; }
  .shimmer-btn::after {
    content: '';
    position: absolute; inset: 0;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.22), transparent);
    background-size: 600px 100%;
    animation: shimmer 2.6s infinite;
    pointer-events: none;
  }
  .spinner {
    width: 16px; height: 16px;
    border: 2.5px solid rgba(255,255,255,0.35);
    border-top-color: #fff;
    border-radius: 50%;
    animation: spin 0.75s linear infinite;
    display: inline-block; vertical-align: middle;
  }
  .particle {
    position: absolute;
    width: 3px; height: 3px;
    border-radius: 50%;
    animation: particleFloat linear infinite;
    pointer-events: none;
  }
`;
document.head.appendChild(styleTag);

/* ─── Animated Canvas Background ─── */
const AnimatedBackground = ({ isDark }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId;
    let W = canvas.width  = window.innerWidth;
    let H = canvas.height = window.innerHeight;

    const onResize = () => {
      W = canvas.width  = window.innerWidth;
      H = canvas.height = window.innerHeight;
    };
    window.addEventListener("resize", onResize);

    /* ── Nodes (floating dots connected by lines) ── */
    const NODE_COUNT = 55;
    const nodes = Array.from({ length: NODE_COUNT }, () => ({
      x: Math.random() * W,
      y: Math.random() * H,
      vx: (Math.random() - 0.5) * 0.45,
      vy: (Math.random() - 0.5) * 0.45,
      r: Math.random() * 2 + 1,
    }));

    const MAX_DIST = 160;

    const draw = () => {
      ctx.clearRect(0, 0, W, H);

      /* move */
      nodes.forEach((n) => {
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < 0 || n.x > W) n.vx *= -1;
        if (n.y < 0 || n.y > H) n.vy *= -1;
      });

      /* connections */
      for (let i = 0; i < NODE_COUNT; i++) {
        for (let j = i + 1; j < NODE_COUNT; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < MAX_DIST) {
            const alpha = (1 - dist / MAX_DIST) * (isDark ? 0.18 : 0.1);
            ctx.beginPath();
            ctx.strokeStyle = isDark
              ? `rgba(99,102,241,${alpha})`
              : `rgba(37,99,235,${alpha})`;
            ctx.lineWidth = 0.8;
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.stroke();
          }
        }
      }

      /* dots */
      nodes.forEach((n) => {
        ctx.beginPath();
        ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2);
        ctx.fillStyle = isDark
          ? "rgba(99,102,241,0.5)"
          : "rgba(37,99,235,0.3)";
        ctx.fill();
      });

      animId = requestAnimationFrame(draw);
    };

    draw();
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", onResize);
    };
  }, [isDark]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        zIndex: 0,
        opacity: isDark ? 1 : 0.7,
      }}
    />
  );
};

/* ─── Floating Particles ─── */
const Particles = ({ isDark }) => {
  const particles = Array.from({ length: 18 }, (_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    size: Math.random() * 3 + 1.5,
    duration: `${Math.random() * 14 + 10}s`,
    delay: `${Math.random() * 8}s`,
    color: isDark
      ? i % 3 === 0 ? "rgba(99,102,241,0.6)" : i % 3 === 1 ? "rgba(37,99,235,0.5)" : "rgba(124,58,237,0.5)"
      : i % 3 === 0 ? "rgba(99,102,241,0.4)" : i % 3 === 1 ? "rgba(37,99,235,0.3)" : "rgba(124,58,237,0.3)",
  }));

  return (
    <>
      {particles.map((p) => (
        <Box
          key={p.id}
          className="particle"
          sx={{
            left: p.left,
            bottom: "-10px",
            width: `${p.size}px`,
            height: `${p.size}px`,
            background: p.color,
            boxShadow: `0 0 ${p.size * 2}px ${p.color}`,
            animationDuration: p.duration,
            animationDelay: p.delay,
          }}
        />
      ))}
    </>
  );
};

/* ─── Theme Factory ─── */
const buildTheme = (isDark) =>
  createTheme({
    typography: { fontFamily: "'Sora', sans-serif" },
    palette: {
      mode: isDark ? "dark" : "light",
      primary: { main: "#2563eb" },
      background: {
        default: isDark ? "#0a0f1e" : "#f0f4ff",
        paper:   isDark ? "#111827" : "#ffffff",
      },
    },
    components: {
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              borderRadius: 14,
              backgroundColor: isDark ? "#1a2236" : "#f8faff",
              fontFamily: "'Sora', sans-serif",
              fontSize: 14,
              transition: "background 0.25s, box-shadow 0.25s",
              "& fieldset": {
                borderColor: isDark ? "#1e3a5f" : "#dde3f5",
                transition: "border-color 0.25s",
              },
              "&:hover fieldset": { borderColor: isDark ? "#2563eb" : "#93a8f0" },
              "&:hover": { backgroundColor: isDark ? "#1e2d45" : "#f1f5ff" },
              "&.Mui-focused fieldset": { borderColor: "#2563eb", borderWidth: "2px" },
              "&.Mui-focused": {
                backgroundColor: isDark ? "#1a2236" : "#fff",
                boxShadow: `0 0 0 4px ${isDark ? "rgba(37,99,235,0.15)" : "rgba(37,99,235,0.08)"}`,
              },
              "&.Mui-error fieldset": { borderColor: "#f87171" },
              "&.Mui-error": { boxShadow: "0 0 0 4px rgba(248,113,113,0.1)" },
            },
            "& .MuiInputBase-input": {
              color: isDark ? "#e2e8f0" : "#0f172a",
              padding: "13px 14px",
              "&::placeholder": { color: isDark ? "#4a5568" : "#94a3b8", opacity: 1 },
            },
            "& .MuiFormHelperText-root": {
              fontFamily: "'Sora', sans-serif",
              fontSize: 11.5, marginTop: 5,
            },
            "& .MuiInputAdornment-root": { color: isDark ? "#4a6080" : "#94a3b8" },
          },
        },
      },
      MuiButton: {
        styleOverrides: { root: { fontFamily: "'Sora', sans-serif" } },
      },
    },
  });

/* ─── Feature Card ─── */
const FeatureCard = ({ icon, title, description, accentColor, className, isDark }) => (
  <Box
    className={className}
    sx={{
      display: "flex", gap: 2,
      p: "16px 20px", mb: 1.5,
      borderRadius: "18px",
      background: isDark
        ? "linear-gradient(135deg, #111827 0%, #1a2236 100%)"
        : "linear-gradient(135deg, #ffffff 0%, #f8faff 100%)",
      border: `1px solid ${isDark ? "#1e3a5f" : "#e8edf8"}`,
      boxShadow: isDark ? "0 2px 12px rgba(0,0,0,0.3)" : "0 2px 12px rgba(37,99,235,0.05)",
      transition: "all 0.25s cubic-bezier(0.22,1,0.36,1)",
      "&:hover": {
        boxShadow: isDark ? "0 8px 28px rgba(37,99,235,0.2)" : "0 8px 28px rgba(37,99,235,0.12)",
        borderColor: alpha(accentColor, isDark ? 0.5 : 0.3),
        transform: "translateY(-3px) translateX(2px)",
      },
    }}
  >
    <Box sx={{
      width: 44, height: 44, borderRadius: "12px", flexShrink: 0,
      background: `linear-gradient(135deg, ${alpha(accentColor, 0.15)}, ${alpha(accentColor, 0.08)})`,
      border: `1px solid ${alpha(accentColor, 0.25)}`,
      display: "flex", alignItems: "center", justifyContent: "center",
      color: accentColor, fontSize: 17,
      boxShadow: `0 4px 12px ${alpha(accentColor, 0.2)}`,
    }}>
      {icon}
    </Box>
    <Box>
      <Typography fontSize={13.5} fontWeight={700} color={isDark ? "#e2e8f0" : "#0f172a"} mb={0.3}>
        {title}
      </Typography>
      <Typography fontSize={12.5} color="#64748b" lineHeight={1.65}>
        {description}
      </Typography>
    </Box>
  </Box>
);

/* ═══════════════════════════════════════
   MAIN COMPONENT
════════════════════════════════════════ */
const SignInForm = () => {
  const navigate  = useNavigate();
  const dispatch  = useAppDispatch();
  const { loading, error, otpRequired, tempToken } = useAppSelector((state) => state.auth);

  const [showPassword, setShowPassword] = useState(false);
  const [credentials, setCredentials]   = useState({ email: "", password: "" });
  const [errors, setErrors]             = useState({ email: "", password: "" });
  const [otp, setOtp]                   = useState("");
  const [otpError, setOtpError]         = useState("");

  /* ── Dark mode ── */
  const [isDark, setIsDark] = useState(() => localStorage.getItem("theme") === "dark");

  useEffect(() => {
    const html = document.querySelector("html");
    if (!html) return;
    if (localStorage.getItem("theme") === "dark") html.classList.add("dark-theme");
    else html.classList.remove("dark-theme");
    const observer = new MutationObserver(() =>
      setIsDark(html.classList.contains("dark-theme"))
    );
    observer.observe(html, { attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);

  const toggleTheme = () => {
    const html = document.querySelector("html");
    const next = html.classList.contains("dark-theme") ? "light" : "dark";
    if (next === "dark") {
      html.classList.add("dark-theme");
      localStorage.setItem("theme", "dark");
      localStorage.setItem("mui-mode", "dark");
    } else {
      html.classList.remove("dark-theme");
      localStorage.setItem("theme", "light");
      localStorage.setItem("mui-mode", "light");
    }
  };

  const theme = buildTheme(isDark);
  const token = useAppSelector((state) => state.auth.token);
  useEffect(() => { if (token) navigate("/"); }, [token, navigate]);

  const validate = () => {
    const temp = {};
    if (!credentials.email) temp.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(credentials.email))
      temp.email = "Enter a valid email address";
    if (!credentials.password) temp.password = "Password is required";
    else if (credentials.password.length < 6)
      temp.password = "Password must be at least 6 characters";
    setErrors(temp);
    return Object.keys(temp).length === 0;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    await dispatch(loginUser(credentials));
    // if no OTP required, token is set → useEffect navigates
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otp.trim() || otp.length !== 6) {
      setOtpError("Enter the 6-digit OTP sent to your email");
      return;
    }
    setOtpError("");
    const result = await dispatch(verifyOtp({ tempToken, otp }));
    if (verifyOtp.rejected.match(result)) {
      setOtpError(result.payload || "Invalid OTP");
    }
    // success → token set → useEffect navigates
  };

  const handleBackToLogin = () => {
    dispatch(cancelOtp());
    setOtp("");
    setOtpError("");
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{
        minHeight: "100vh",
        background: isDark
          ? "linear-gradient(145deg, #060b16 0%, #0c1220 50%, #080e1a 100%)"
          : "linear-gradient(145deg, #eef2ff 0%, #f5f7ff 50%, #e8f0fe 100%)",
        display: "flex", alignItems: "center", justifyContent: "center",
        px: { xs: 2, md: 4 }, py: 6,
        position: "relative", overflow: "hidden",
        transition: "background 0.4s ease",
      }}>

        {/* ══ THEME TOGGLE ══ */}
        <IconButton
          onClick={toggleTheme}
          sx={{
            position: "fixed", top: 16, right: 16, zIndex: 10,
            width: 42, height: 42,
            background: isDark
              ? "linear-gradient(135deg, #111827, #1a2236)"
              : "linear-gradient(135deg, #ffffff, #f0f4ff)",
            border: `1px solid ${isDark ? "#1e3a5f" : "#dde3f5"}`,
            boxShadow: isDark
              ? "0 4px 16px rgba(0,0,0,0.4)"
              : "0 4px 16px rgba(37,99,235,0.1)",
            color: isDark ? "#facc15" : "#2563eb",
            transition: "all 0.25s ease",
            "&:hover": {
              transform: "scale(1.08)",
              boxShadow: isDark
                ? "0 6px 20px rgba(0,0,0,0.5)"
                : "0 6px 20px rgba(37,99,235,0.18)",
            },
          }}
        >
          {isDark ? <FaSun size={16} /> : <FaMoon size={16} />}
        </IconButton>

        {/* ══ ANIMATED CANVAS NETWORK ══ */}
        <AnimatedBackground isDark={isDark} />

        {/* ══ FLOATING PARTICLES ══ */}
        <Particles isDark={isDark} />

        {/* ══ LIGHT BEAM SWEEP ══ */}
        <Box className="beam-sweep" sx={{
          position: "absolute",
          top: "-20%", left: "-10%",
          width: "40%", height: "140%",
          background: isDark
            ? "linear-gradient(90deg, transparent, rgba(99,102,241,0.04), transparent)"
            : "linear-gradient(90deg, transparent, rgba(37,99,235,0.04), transparent)",
          pointerEvents: "none", zIndex: 0,
        }} />

        {/* ══ LARGE BLURRED RINGS ══ */}
        <Box className="ring-expand" sx={{
          position: "absolute", top: "10%", right: "5%",
          width: 280, height: 280,
          borderRadius: "50%",
          border: `1px solid ${isDark ? "rgba(99,102,241,0.15)" : "rgba(37,99,235,0.1)"}`,
          pointerEvents: "none", zIndex: 0,
        }} />
        <Box className="ring-expand" sx={{
          position: "absolute", top: "10%", right: "5%",
          width: 280, height: 280,
          borderRadius: "50%",
          border: `1px solid ${isDark ? "rgba(99,102,241,0.1)" : "rgba(37,99,235,0.07)"}`,
          pointerEvents: "none", zIndex: 0,
          animationDelay: "1s",
        }} />
        <Box className="ring-expand" sx={{
          position: "absolute", bottom: "15%", left: "8%",
          width: 200, height: 200,
          borderRadius: "50%",
          border: `1px solid ${isDark ? "rgba(124,58,237,0.15)" : "rgba(124,58,237,0.1)"}`,
          pointerEvents: "none", zIndex: 0,
          animationDelay: "1.5s",
        }} />

        {/* ══ AMBIENT ORBS ══ */}
        <Box className="orb-a" sx={{
          position: "absolute", top: -180, right: -140,
          width: 580, height: 580, borderRadius: "50%",
          background: isDark
            ? "radial-gradient(circle, rgba(99,102,241,0.14) 0%, transparent 65%)"
            : "radial-gradient(circle, rgba(99,102,241,0.1) 0%, transparent 65%)",
          pointerEvents: "none", zIndex: 0,
        }} />
        <Box className="orb-b" sx={{
          position: "absolute", bottom: -100, left: -120,
          width: 450, height: 450, borderRadius: "50%",
          background: isDark
            ? "radial-gradient(circle, rgba(37,99,235,0.14) 0%, transparent 65%)"
            : "radial-gradient(circle, rgba(37,99,235,0.08) 0%, transparent 65%)",
          pointerEvents: "none", zIndex: 0,
        }} />
        <Box sx={{
          position: "absolute", top: "40%", left: "30%",
          width: 300, height: 300, borderRadius: "50%",
          background: isDark
            ? "radial-gradient(circle, rgba(124,58,237,0.07) 0%, transparent 70%)"
            : "radial-gradient(circle, rgba(124,58,237,0.04) 0%, transparent 70%)",
          pointerEvents: "none", zIndex: 0,
          display: { xs: "none", lg: "block" },
        }} />

        {/* ══ GRID CONTENT ══ */}
        <Grid
          container maxWidth="lg"
          spacing={{ xs: 4, lg: 8 }}
          alignItems="center"
          sx={{ position: "relative", zIndex: 1 }}
        >

          {/* ══════════ LEFT — Login Card ══════════ */}
          <Grid item xs={12} lg={5}>
            <Box
              className="card-animate"
              component="form"
              onSubmit={otpRequired ? handleVerifyOtp : handleLogin}
              sx={{
                background: isDark
                  ? "linear-gradient(160deg, #0d1220 0%, #0b1018 100%)"
                  : "linear-gradient(160deg, #ffffff 0%, #fbfcff 100%)",
                border: `1px solid ${isDark ? "#182840" : "rgba(99,102,241,0.1)"}`,
                borderRadius: "32px",
                boxShadow: isDark
                  ? "0 40px 120px rgba(0,0,0,0.65), 0 8px 40px rgba(0,0,0,0.5)"
                  : "0 40px 120px rgba(99,102,241,0.1), 0 8px 40px rgba(0,0,0,0.06)",
                p: { xs: "36px 28px", sm: "60px 56px" },
                maxWidth: 560, mx: "auto",
                transition: "all 0.4s ease",
                position: "relative", overflow: "hidden",
                backdropFilter: "blur(2px)",
              }}
            >

              {/* ── Logo ── */}
              <Box className="logo-animate" sx={{
                display: "flex", flexDirection: "column",
                alignItems: "center", mb: 5,
              }}>
                <Box sx={{
                  display: "inline-flex",
                  alignItems: "center", justifyContent: "center",
                  px: 1, py: 1, borderRadius: "22px",
                  background: isDark
                    ? "linear-gradient(135deg, #111828 0%, #0c1420 100%)"
                    : "linear-gradient(135deg, #eef2ff 0%, #e8eeff 100%)",
                  border: `1px solid ${isDark ? "#1a2d48" : "#dde3f8"}`,
                  boxShadow: isDark
                    ? "0 10px 32px rgba(0,0,0,0.4)"
                    : "0 10px 32px rgba(99,102,241,0.12)",
                  mb: 3.5,
                }}>
                  <img
                    src={isDark
                      ? "/logo/NextternSMSFullLogo-ForDarkBg.png"
                      : "/logo/NextternSMSFullLogo-ForLightBg.png"
                    }
                    alt="EduNext"
                    style={{ width: "270px", height: "auto", display: "block" }}
                  />
                </Box>
                <Typography variant="h4" fontWeight={800}
                  color={isDark ? "#e8edf5" : "#0f172a"}
                  letterSpacing="-0.03em" textAlign="center" mb={1}>
                  Welcome back
                </Typography>
                <Typography fontSize={14.5}
                  color={isDark ? "#2a4060" : "#64748b"}
                  textAlign="center" lineHeight={1.7}>
                  Sign in to your admin workspace
                </Typography>
              </Box>

              {/* ── Divider with pill ── */}
              <Box sx={{ position: "relative", mb: 4.5 }}>
                <Divider sx={{ borderColor: isDark ? "#162030" : "#eef2ff" }} />
                <Box sx={{
                  position: "absolute", top: "50%", left: "50%",
                  transform: "translate(-50%, -50%)",
                  px: 2.5, py: 0.6,
                  background: isDark ? "#0d1220" : "#ffffff",
                  borderRadius: "999px",
                  border: `1px solid ${isDark ? "#162030" : "#eef2ff"}`,
                  whiteSpace: "nowrap",
                }}>
                  <Typography fontSize={10.5} fontWeight={700}
                    color={isDark ? "#1e3050" : "#c4cfe0"}
                    letterSpacing="0.1em" textTransform="uppercase">
                    Admin Access
                  </Typography>
                </Box>
              </Box>

              {/* ════ STEP 1: Credentials ════ */}
              {!otpRequired && (<>

              {/* ── Email ── */}
              <Box mb={3}>
                <Typography fontSize={11.5} fontWeight={700}
                  color={isDark ? "#2a4060" : "#475569"}
                  mb={1.2} letterSpacing="0.09em" textTransform="uppercase">
                  Email Address
                </Typography>
                <TextField
                  fullWidth
                  placeholder="admin@company.com"
                  value={credentials.email}
                  error={!!errors.email}
                  helperText={errors.email}
                  onChange={(e) => {
                    setCredentials({ ...credentials, email: e.target.value });
                    setErrors({ ...errors, email: "" });
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FaUserShield size={15}
                          color={credentials.email ? "#2563eb" : isDark ? "#2a4060" : "#b0bdd0"} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {/* ── Password ── */}
              <Box mb={2}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.2}>
                  <Typography fontSize={11.5} fontWeight={700}
                    color={isDark ? "#2a4060" : "#475569"}
                    letterSpacing="0.09em" textTransform="uppercase">
                    Password
                  </Typography>
                  {/* <Typography
                    component="span"
                    onClick={() => {
                      Swal.fire({
                        title: "Password Reset Required",
                        icon: "info",
                        background: isDark ? "#0d1220" : "#ffffff",
                        color: isDark ? "#e2e8f0" : "#0f172a",
                        confirmButtonColor: "#2563eb",
                        showCancelButton: true,
                        cancelButtonColor: isDark ? "#1e293b" : "#e2e8f0",
                        confirmButtonText: "Send Email",
                        cancelButtonText: "Close",
                        width: 440, padding: "2em",
                        html: `
                          <div style="text-align:center; font-family:'Sora',sans-serif;">
                            <div style="
                              background: linear-gradient(135deg,#2563eb,#7c3aed);
                              width:64px; height:64px; border-radius:50%;
                              display:flex; align-items:center; justify-content:center;
                              margin:0 auto 16px;
                              box-shadow:0 10px 24px rgba(37,99,235,0.45);
                              font-size:28px;
                            ">🔐</div>
                            <p style="margin-bottom:16px; color:#64748b; font-size:13.5px; line-height:1.7;">
                              Contact your Super Admin to reset your password.
                            </p>
                            <div style="
                              background:${isDark ? "#111828" : "#f7f9ff"};
                              border:1px solid ${isDark ? "#1a2d48" : "#e0e7ff"};
                              border-radius:14px; padding:16px; text-align:left;
                            ">
                              <div style="font-weight:700; font-size:14px; margin-bottom:4px;
                                color:${isDark ? "#e2e8f0" : "#0f172a"};">
                                👤 Harsh Prajapati
                              </div>
                              <div style="font-size:12px; color:#64748b; margin-bottom:8px;">Super Admin</div>
                              <a href="mailto:harsh@nexttern.in"
                                style="color:#3b82f6; text-decoration:none; font-size:13px;">
                                ✉ harsh@nexttern.in
                              </a>
                            </div>
                          </div>
                        `,
                      }).then((result) => {
                        if (result.isConfirmed) window.location.href = "mailto:harsh@nexttern.in";
                      });
                    }}
                    fontSize={12.5}
                    sx={{
                      color: "#2563eb", fontWeight: 600, cursor: "pointer",
                      transition: "all 0.2s",
                      "&:hover": { color: "#7c3aed", textDecoration: "underline" },
                    }}
                  >
                    Forgot password?
                  </Typography> */}
                </Box>
                <TextField
                  fullWidth
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={credentials.password}
                  error={!!errors.password}
                  helperText={errors.password}
                  onChange={(e) => {
                    setCredentials({ ...credentials, password: e.target.value });
                    setErrors({ ...errors, password: "" });
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <FaLock size={14}
                          color={credentials.password ? "#2563eb" : isDark ? "#2a4060" : "#b0bdd0"} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end" size="small"
                          sx={{
                            color: isDark ? "#2a4060" : "#b0bdd0",
                            borderRadius: "10px", mr: 0.2,
                            transition: "all 0.2s",
                            "&:hover": { color: "#2563eb", background: alpha("#2563eb", 0.08) },
                          }}
                        >
                          {showPassword ? <FaEyeSlash size={14} /> : <FaEye size={14} />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {/* ── Forgot password link ── */}
              <Box sx={{ textAlign: "right", mb: 1 }}>
                <Box
                  component="span"
                  onClick={() => navigate("/forgot-password")}
                  sx={{ fontSize: 12, color: "#6366f1", cursor: "pointer", fontWeight: 600,
                    "&:hover": { textDecoration: "underline" } }}
                >
                  Forgot password?
                </Box>
              </Box>

              {/* ── Error ── */}
              {error && (
                <Box sx={{
                  mb: 2.5, p: "13px 18px", borderRadius: "14px",
                  background: isDark ? alpha("#f87171", 0.07) : alpha("#fef2f2", 0.9),
                  border: `1px solid ${alpha("#f87171", 0.22)}`,
                  display: "flex", alignItems: "center", gap: 1.5,
                }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: "#f87171", flexShrink: 0 }} />
                  <Typography color="error" fontSize={13} fontWeight={500} lineHeight={1.5}>
                    {error}
                  </Typography>
                </Box>
              )}

              {/* ── Submit ── */}
              <Button
                type="submit" fullWidth disabled={loading}
                className="shimmer-btn"
                endIcon={!loading && <FaArrowRight size={14} />}
                sx={{
                  mt: 1.5, py: 2,
                  borderRadius: "16px",
                  fontWeight: 700, fontSize: 15.5,
                  textTransform: "none", letterSpacing: "0.01em",
                  background: "linear-gradient(135deg, #2563eb 0%, #4f46e5 55%, #7c3aed 100%)",
                  color: "#fff",
                  position: "relative", overflow: "hidden",
                  boxShadow: "0 12px 32px rgba(37,99,235,0.35), 0 4px 12px rgba(37,99,235,0.2)",
                  transition: "all 0.22s ease",
                  "&:hover:not(:disabled)": {
                    background: "linear-gradient(135deg, #1d4ed8 0%, #4338ca 55%, #6d28d9 100%)",
                    transform: "translateY(-2px)",
                    boxShadow: "0 20px 44px rgba(37,99,235,0.42), 0 6px 16px rgba(37,99,235,0.25)",
                  },
                  "&:active:not(:disabled)": {
                    transform: "translateY(0)",
                    boxShadow: "0 8px 20px rgba(37,99,235,0.3)",
                  },
                  "&:disabled": {
                    opacity: 0.65, color: "#fff",
                    background: "linear-gradient(135deg, #2563eb, #7c3aed)",
                  },
                }}
              >
                {loading ? (
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <span className="spinner" />
                    Authenticating...
                  </Box>
                ) : "Sign In to Dashboard"}
              </Button>

              </>)}

              {/* ════ STEP 2: OTP Verification ════ */}
              {otpRequired && (<>

              {/* OTP icon + heading */}
              <Box sx={{ textAlign: "center", mb: 3.5 }}>
                <Box sx={{
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  width: 64, height: 64, borderRadius: "20px", mb: 2,
                  background: isDark
                    ? "linear-gradient(135deg, #1a2236, #111828)"
                    : "linear-gradient(135deg, #eef2ff, #e8eeff)",
                  border: `1px solid ${isDark ? "#1a2d48" : "#dde3f8"}`,
                  boxShadow: "0 8px 24px rgba(37,99,235,0.18)",
                }}>
                  <MdLockClock size={28} color="#2563eb" />
                </Box>
                <Typography fontSize={18} fontWeight={800} color={isDark ? "#e8edf5" : "#0f172a"} mb={0.5}>
                  Verify Your Identity
                </Typography>
                <Typography fontSize={13} color={isDark ? "#2a4060" : "#64748b"} lineHeight={1.7}>
                  A 6-digit OTP has been sent to your email.<br />
                  It expires in <strong>5 minutes</strong>.
                </Typography>
              </Box>

              {/* OTP input */}
              <Box mb={2.5}>
                <Typography fontSize={11.5} fontWeight={700}
                  color={isDark ? "#2a4060" : "#475569"}
                  mb={1.2} letterSpacing="0.09em" textTransform="uppercase">
                  One-Time Password
                </Typography>
                <TextField
                  fullWidth
                  autoFocus
                  placeholder="Enter 6-digit OTP"
                  value={otp}
                  error={!!otpError}
                  helperText={otpError}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, "").slice(0, 6);
                    setOtp(v);
                    setOtpError("");
                  }}
                  inputProps={{ inputMode: "numeric", maxLength: 6 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MdLockClock size={16}
                          color={otp ? "#2563eb" : isDark ? "#2a4060" : "#b0bdd0"} />
                      </InputAdornment>
                    ),
                    sx: { letterSpacing: "0.35em", fontSize: 18, fontWeight: 700 },
                  }}
                />
              </Box>

              {/* Redux error */}
              {error && (
                <Box sx={{
                  mb: 2.5, p: "13px 18px", borderRadius: "14px",
                  background: isDark ? alpha("#f87171", 0.07) : alpha("#fef2f2", 0.9),
                  border: `1px solid ${alpha("#f87171", 0.22)}`,
                  display: "flex", alignItems: "center", gap: 1.5,
                }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: "#f87171", flexShrink: 0 }} />
                  <Typography color="error" fontSize={13} fontWeight={500} lineHeight={1.5}>
                    {error}
                  </Typography>
                </Box>
              )}

              {/* Verify button */}
              <Button
                type="submit" fullWidth disabled={loading}
                className="shimmer-btn"
                endIcon={!loading && <FaArrowRight size={14} />}
                sx={{
                  mt: 0.5, py: 2,
                  borderRadius: "16px",
                  fontWeight: 700, fontSize: 15.5,
                  textTransform: "none",
                  background: "linear-gradient(135deg, #2563eb 0%, #4f46e5 55%, #7c3aed 100%)",
                  color: "#fff",
                  position: "relative", overflow: "hidden",
                  boxShadow: "0 12px 32px rgba(37,99,235,0.35)",
                  transition: "all 0.22s ease",
                  "&:hover:not(:disabled)": {
                    background: "linear-gradient(135deg, #1d4ed8 0%, #4338ca 55%, #6d28d9 100%)",
                    transform: "translateY(-2px)",
                  },
                  "&:disabled": { opacity: 0.65, color: "#fff", background: "linear-gradient(135deg, #2563eb, #7c3aed)" },
                }}
              >
                {loading ? (
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <span className="spinner" />
                    Verifying...
                  </Box>
                ) : "Verify OTP"}
              </Button>

              {/* Back link */}
              <Box sx={{ textAlign: "center", mt: 2.5 }}>
                <Typography
                  component="span"
                  fontSize={13} fontWeight={600}
                  sx={{
                    color: "#2563eb", cursor: "pointer",
                    "&:hover": { color: "#7c3aed", textDecoration: "underline" },
                  }}
                  onClick={handleBackToLogin}
                >
                  ← Back to Sign In
                </Typography>
              </Box>

              </>)}

            </Box>
          </Grid>

          {/* ══════════ RIGHT — Info Panel ══════════ */}
          <Grid item xs={12} lg={7} display={{ xs: "none", lg: "block" }}>

            {/* Live badge */}
            <Box sx={{
              display: "inline-flex", alignItems: "center", gap: 1,
              background: isDark
                ? "linear-gradient(135deg, #111827, #1a2236)"
                : "linear-gradient(135deg, #ffffff, #f8faff)",
              border: `1px solid ${isDark ? "#1e3a5f" : "#e2e8f0"}`,
              borderRadius: "999px",
              px: 2.5, py: 1, mb: 4,
              boxShadow: isDark ? "0 4px 16px rgba(0,0,0,0.3)" : "0 4px 16px rgba(37,99,235,0.08)",
            }}>
              <Box className="live-dot" sx={{
                width: 8, height: 8, borderRadius: "50%",
                background: "#22c55e", flexShrink: 0,
              }} />
              <Typography fontSize={12.5} color="#64748b" fontWeight={600}>
                System Operational · All services running
              </Typography>
            </Box>

            {/* Headline */}
            <Typography variant="h3" fontWeight={800}
              color={isDark ? "#e2e8f0" : "#0f172a"}
              letterSpacing="-0.03em" lineHeight={1.12} mb={2}>
              Secure{" "}
              <Box component="span" sx={{
                background: "linear-gradient(90deg, #2563eb, #4f46e5, #7c3aed)",
                WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
              }}>
                Admin Access
              </Box>
              <br />
              <Box component="span" sx={{
                fontSize: "0.6em", fontWeight: 600,
                color: isDark ? "#475569" : "#94a3b8",
              }}>
                Built for enterprise teams
              </Box>
            </Typography>

            <Typography fontSize={15} color={isDark ? "#475569" : "#64748b"}
              lineHeight={1.85} mb={4.5} sx={{ maxWidth: 400 }}>
              Your centralized hub for managing enterprise operations, analytics,
              and team permissions — all in one place.
            </Typography>

            {/* Features */}
            <FeatureCard className="feature-1" icon={<FaUsersCog />}
              title="Centralized Control"
              description="Manage users, roles, and permissions from a single unified dashboard"
              accentColor="#4f46e5" isDark={isDark} />
            <FeatureCard className="feature-2" icon={<FaShieldAlt />}
              title="Enterprise Security"
              description="JWT authentication, RBAC protection, and full audit logging"
              accentColor="#2563eb" isDark={isDark} />
            <FeatureCard className="feature-3" icon={<FaChartLine />}
              title="Analytics Dashboard"
              description="Monitor real-time performance metrics and business insights"
              accentColor="#059669" isDark={isDark} />

           
          </Grid>
        </Grid>

        {/* Footer */}
        <Typography sx={{
          position: "absolute", bottom: 16, left: 0, right: 0,
          textAlign: "center", fontSize: 12,
          color: isDark ? "#2d3f5a" : "#cbd5e1",
          letterSpacing: "0.03em", fontWeight: 500, zIndex: 1,
        }}>
          © 2026 EduNext. All Rights Reserved.
        </Typography>
      </Box>
    </ThemeProvider>
  );
};

export default SignInForm;